import * as React from "react";
import { chipClasses } from "@mui/material/Chip";
import type { Components } from "@mui/material/styles";
import { XCircleIcon } from "@phosphor-icons/react/dist/ssr/XCircle";

import type { Theme } from "../types";

function getSoftVars(color: string, dark: boolean): Record<string, string> {
	if (dark) {
		return {
			"--Chip-softBg": `var(--mui-palette-${color}-800)`,
			"--Chip-softColor": `var(--mui-palette-${color}-200)`,
			"--Chip-softDisabledBg": `var(--mui-palette-${color}-800)`,
			"--Chip-softDisabledColor": `var(--mui-palette-${color}-500)`,
			"--Chip-softHoverBg": `var(--mui-palette-${color}-700)`,
			"--Chip-softDeleteIconColor": `var(--mui-palette-${color}-200)`,
			"--Chip-softDeleteIconHoverColor": `var(--mui-palette-${color}-50)`,
		};
	}

	return {
		"--Chip-softBg": `var(--mui-palette-${color}-100)`,
		"--Chip-softColor": `var(--mui-palette-${color}-700)`,
		"--Chip-softDisabledBg": `var(--mui-palette-${color}-50)`,
		"--Chip-softDisabledColor": `var(--mui-palette-${color}-400)`,
		"--Chip-softHoverBg": `var(--mui-palette-${color}-200)`,
		"--Chip-softDeleteIconColor": `var(--mui-palette-${color}-700)`,
		"--Chip-softDeleteIconHoverColor": `var(--mui-palette-${color}-800)`,
	};
}

export const MuiChip = {
	defaultProps: {
		color: "secondary", // default will be removed in material v6
		deleteIcon: <XCircleIcon />,
	},
	styleOverrides: {
		root: { borderRadius: "12px", fontWeight: 500 },
		// Custom case for secondary, the rest is handled by the theme.
		//
		// Keyed on `applyStyles` rather than `palette.mode`. This theme runs in CSS-variables
		// mode, where `mode` is resolved once at build time and always reports the default
		// scheme - so this branch never fired and a dark-mode outlined chip was given
		// `secondary-900` for its label. That is the same colour as `background.paper` in the
		// dark scheme: 1.00:1, an empty outline with invisible text. Chips are `secondary` by
		// default, so it hit every outlined chip that did not name a colour of its own.
		outlinedSecondary: ({ theme }) => ({
			borderColor: "var(--mui-palette-secondary-200)",
			color: "var(--mui-palette-secondary-900)",
			...theme.applyStyles("dark", {
				borderColor: "var(--mui-palette-secondary-700)",
				color: "var(--mui-palette-secondary-50)",
			}),
		}),
		soft: ({ ownerState }) => {
			return {
				backgroundColor: "var(--Chip-softBg)",
				color: "var(--Chip-softColor)",
				...(ownerState.disabled && {
					backgroundColor: "var(--Chip-softDisabledBg)",
					color: "var(--Chip-softDisabledColor)",
				}),
				...(ownerState.clickable && { "&:hover": { backgroundColor: "var(--Chip-softHoverBg)" } }),
				[`& .${chipClasses.deleteIcon}`]: {
					color: "var(--Chip-softDeleteIconColor)",
					"&:hover": { color: "var(--Chip-softDeleteIconHoverColor)" },
				},
				"&.Mui-focusVisible": { backgroundColor: "var(--Chip-softHoverBg)" },
			};
		},
		softPrimary: ({ theme }) => ({
			...getSoftVars("primary", false),
			...theme.applyStyles("dark", getSoftVars("primary", true)),
		}),
		softSecondary: ({ theme }) => ({
			...getSoftVars("secondary", false),
			...theme.applyStyles("dark", getSoftVars("secondary", true)),
		}),
		softSuccess: ({ theme }) => ({
			...getSoftVars("success", false),
			...theme.applyStyles("dark", getSoftVars("success", true)),
		}),
		softInfo: ({ theme }) => ({
			...getSoftVars("info", false),
			...theme.applyStyles("dark", getSoftVars("info", true)),
		}),
		softWarning: ({ theme }) => ({
			...getSoftVars("warning", false),
			...theme.applyStyles("dark", getSoftVars("warning", true)),
		}),
		softError: ({ theme }) => ({
			...getSoftVars("error", false),
			...theme.applyStyles("dark", getSoftVars("error", true)),
		}),
		iconSmall: { fontSize: "var(--icon-fontSize-sm)" },
		iconMedium: { fontSize: "var(--icon-fontSize-md)" },
	},
} satisfies Components<Theme>["MuiChip"];
